// deps
import { useCallback, useMemo } from "react";

// internal-deps
import {
    CardPaymentMethodData,
    CardSpecificActions,
    FoodCardData,
    ICardPaymentGroup,
} from "@swiggy-private/payment-ui";
import { ApiFetchState } from "@swiggy-private/payment-core";

// interfaces
import { PaymentGroupHandlerProps } from "@payment-client/components/show-payments/components/payment-group-handler/interfaces";

// hooks
import { useCardBalanceData } from "@payment-client/hooks/UseCardBalanceData";
import useCardEvents from "@payment-client/hooks/UseCardEvents";
import { usePaymentActions } from "./UsePaymentActions";

// helpers
import { getPaymentCardReference } from "@payment-client/helpers/PaymentHelper";

// constants
import { SCREEN_NAME } from "@payment-client/analytics/constants";

interface ReturnType {
    paymentActions: ICardPaymentGroup["paymentActions"];
    cardSpecificActions: CardSpecificActions;
}

interface UseCardHandlerProps {
    onPay: PaymentGroupHandlerProps["onPay"];
    onSelect: PaymentGroupHandlerProps["onSelect"];
    paymentAmount: string;
}

export const useCardHandler = (props: UseCardHandlerProps): ReturnType => {
    const { onSelect, onPay, paymentAmount } = props;

    const actions = usePaymentActions({ onSelect, onPay });
    const cardBalanceState = useCardBalanceData({
        paymentAmount: Number(paymentAmount),
    });

    const cardEvents = useCardEvents(SCREEN_NAME.PAYMENT);

    const getCardAmount = useCallback(
        (methodData: CardPaymentMethodData): [ApiFetchState, FoodCardData] | null => {
            const cardReference = getPaymentCardReference(methodData);
            const state = cardBalanceState[cardReference];

            return state ? [state.fetchState, state.data] : null;
        },
        [cardBalanceState],
    );

    const cardSpecificActions = useMemo(
        (): CardSpecificActions => ({
            ...cardEvents,
            getCardAmount,
        }),
        [cardEvents, getCardAmount],
    );

    const paymentActions = useMemo(
        (): ICardPaymentGroup["paymentActions"] => ({
            ...actions,
            ...cardSpecificActions,
        }),
        [actions, cardSpecificActions],
    );

    return { paymentActions, cardSpecificActions };
};
